import React from 'react';
import { useNavigate } from 'react-router';
import MagicButton from 'components/MagicButton/MagicButton';
import { AssetUrls } from 'config/assets';
import { ActiveTradesPanelResponse } from 'store/services/trade/tradeApi';
import { EMPTY_STATE_COPY, sendableSentence, transitFootnote } from './emptyStateCopy';
import { PanelDirection, PanelStatus } from './types';
import './TradeTabEmptyState.css';

interface Props {
  direction: PanelDirection;
  status: PanelStatus;
  /** Absent when the panel endpoint is unavailable; the copy still renders */
  data?: ActiveTradesPanelResponse;
  isMobile: boolean;
  /** 'list' is the collapsed-panel placement under the tabs: no map, no fixed height */
  placement?: 'panel' | 'list';
}

/** Heading, body and footnote for one tab. Shared with the In Transit map variant. */
export const EmptyStateText: React.FC<Pick<Props, 'direction' | 'status' | 'data'>> = ({ direction, status, data }) => {
  const copy = EMPTY_STATE_COPY[direction][status];
  const lead = direction === 'outbound' && status === 'ready' ? sendableSentence(data?.sendableCardCount) : null;
  const footnote = status === 'transit' && data ? transitFootnote(data.history) : null;
  return (
    <>
      <h3 className="ttp-empty-heading">{copy.heading}</h3>
      {copy.body && <p className="ttp-empty-body">{lead ? `${lead} ${copy.body}` : copy.body}</p>}
      {footnote && <p className="ttp-empty-footnote">{footnote}</p>}
    </>
  );
};

/**
 * The centred empty state. Imported by the pages as well as the panel, so it
 * must not import the map: that would pull the outline into the main bundle.
 * The In Transit map variant lives in TransitMapEmptyState, inside the lazy chunk.
 */
const TradeTabEmptyState: React.FC<Props> = ({ direction, status, data, isMobile, placement = 'panel' }) => {
  const navigate = useNavigate();
  const copy = EMPTY_STATE_COPY[direction][status];
  const cta = copy.cta;
  const text = <EmptyStateText direction={direction} status={status} data={data} />;

  return (
    <div className={`ttp-empty ttp-empty--centred ttp-empty--${placement}`} data-testid="ttp-empty">
      <img className="ttp-empty-mark" src={AssetUrls.logos.arevakhachIcon} alt="" width={40} height={40} />
      {text}
      {cta && (
        <div className="ttp-empty-cta">
          <MagicButton
            label={cta.label}
            color="purple"
            fullWidth={isMobile}
            onPress={() => navigate(cta.to)}
          />
        </div>
      )}
    </div>
  );
};

export default TradeTabEmptyState;
